import * as React from "react";
import { Modal } from "react-bootstrap";
import { gettext } from "i18n";
import MfaCodeForm from "./MfaCodeForm";
import {
  METHOD_BACKUP,
  MfaStatus,
  VerificationMethod,
  beginStepUp,
  isStepUpRequired,
  loadMfaStatus,
  verifyStepUp,
} from "utils/mfa";

export class StepUpCancelled extends Error {
  constructor() {
    super(gettext("Cancelled"));
  }
}

type WithStepUp = <T>(action: () => Promise<T>) => Promise<T>;

interface StepUpContextValue {
  withStepUp: WithStepUp;
  // Asks the user to confirm their second factor now; rejects with
  // StepUpCancelled if they close the dialog
  confirmSecondFactor: () => Promise<void>;
}

const StepUpContext = React.createContext<StepUpContextValue>({
  withStepUp: (action) => action(),
  confirmSecondFactor: async () => {},
});

/**
 * Runs an action that may need the second factor confirmed first (e.g. creating
 * an API token or changing the password): if the server asks for it, the user
 * confirms it in a dialog and the action is run again.
 */
export const useStepUp = (): WithStepUp =>
  React.useContext(StepUpContext).withStepUp;

export const useConfirmSecondFactor = () =>
  React.useContext(StepUpContext).confirmSecondFactor;

interface PendingStepUp {
  status: MfaStatus;
  resolve: () => void;
  reject: (e: Error) => void;
}

const verificationMethods = (status: MfaStatus): VerificationMethod[] => {
  const kinds: VerificationMethod[] = [];
  for (const method of status.methods) {
    if (!kinds.includes(method.kind)) {
      kinds.push(method.kind);
    }
  }
  if (status.backup_codes_remaining > 0) {
    kinds.push(METHOD_BACKUP);
  }
  return kinds;
};

export const StepUpProvider: React.FC = ({ children }) => {
  const [pending, setPending] = React.useState<PendingStepUp | null>(null);

  const confirmSecondFactor = React.useCallback(
    async () =>
      new Promise<void>(async (resolve, reject) => {
        try {
          const status = await loadMfaStatus();
          setPending({ status, resolve, reject });
        } catch (e) {
          reject(e as Error);
        }
      }),
    [],
  );

  const withStepUp: WithStepUp = React.useCallback(
    async (action) => {
      try {
        return await action();
      } catch (e) {
        if (!isStepUpRequired(e)) {
          throw e;
        }
      }
      await confirmSecondFactor();
      return action();
    },
    [confirmSecondFactor],
  );

  const close = (verified: boolean) => {
    if (!pending) {
      return;
    }
    if (verified) {
      pending.resolve();
    } else {
      pending.reject(new StepUpCancelled());
    }
    setPending(null);
  };

  const onVerify = async (method: VerificationMethod, code?: string) => {
    try {
      await verifyStepUp(method, code);
      close(true);
      return null;
    } catch (e) {
      return gettext((e as Error).message);
    }
  };

  const contextValue = React.useMemo(
    () => ({ withStepUp, confirmSecondFactor }),
    [withStepUp, confirmSecondFactor],
  );

  return (
    <StepUpContext.Provider value={contextValue}>
      {children}
      <Modal show={!!pending} onHide={() => close(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{gettext("Confirm it's you")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {gettext(
              "This change needs your second factor. Please confirm it to continue.",
            )}
          </p>
          {pending && (
            <MfaCodeForm
              methods={verificationMethods(pending.status)}
              preferred={pending.status.preferred_method}
              emailHint={pending.status.email_hint}
              onRequestEmail={async () => {
                await beginStepUp("email");
              }}
              onVerify={onVerify}
              submitLabel={gettext("Confirm")}
            />
          )}
        </Modal.Body>
      </Modal>
    </StepUpContext.Provider>
  );
};

export default StepUpProvider;
